import React from 'react';
import { Award, Target, Heart, Users as UsersIcon, TrendingUp, Globe, Mail, Phone, Linkedin, Youtube, Instagram } from 'lucide-react';
import { mockData } from '../data/mock';
import OptimizedImage from '../components/OptimizedImage';

const About = () => {
  const { company, stats } = mockData;

  const values = [
    {
      icon: Heart,
      title: "Passion for Excellence",
      description: "Every event is crafted with meticulous attention to detail and unwavering commitment to perfection."
    },
    {
      icon: UsersIcon,
      title: "Client-Centric Approach",
      description: "Your vision drives our creativity. We listen, understand, and deliver beyond expectations."
    },
    {
      icon: Globe,
      title: "Global Standards",
      description: "Combining international expertise with local Thai elegance to create world-class experiences."
    },
    {
      icon: TrendingUp,
      title: "Innovation & Creativity",
      description: "Pushing boundaries with cutting-edge concepts and innovative event solutions."
    },
    {
      icon: Award,
      title: "Proven Track Record",
      description: "Over a decade of excellence with 500+ successful events and 25+ industry awards."
    },
    {
      icon: Target,
      title: "Seamless Execution",
      description: "From concept to completion, we ensure flawless coordination and timely delivery."
    }
  ];

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/PC9.jpg"
            alt="About Us"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">About Prestige Events</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Creating Moments That
            <span className="gold-gradient-text block mt-2">Last Forever</span>
          </h1>
          {/* Golden Decorative Line */}
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg" style={{
              boxShadow: '0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)'
            }}>
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg" style={{
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.3)'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  Founded over a decade ago in the heart of Bangkok, Prestige Events began with a simple yet 
                  powerful vision: to transform ordinary occasions into extraordinary experiences that resonate 
                  for a lifetime.
                </p>
                <p>
                  What started as a boutique event planning service has evolved into Thailand's premier luxury 
                  event company, trusted by corporations, celebrities, and discerning clients across Asia and beyond. 
                  Our journey has been marked by countless unforgettable moments, from intimate beach weddings to 
                  grand international conferences.
                </p>
                <p>
                  Today, we stand as a testament to what passion, creativity, and unwavering commitment to excellence 
                  can achieve. With a team of seasoned professionals and a network spanning the globe, we continue 
                  to set new standards in luxury event management.
                </p>
              </div>
            </div>

            <div className="relative h-[500px]">
              <OptimizedImage
                src="/about.jpg"
                alt="Our Story"
                className="rounded-sm h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-gold/5 rounded-sm -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder & CEO Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Leadership</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Meet Our
              <span className="gold-gradient-text"> Founder & CEO</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="relative h-[500px] order-2 lg:order-1">
              <OptimizedImage
                src="/PC21.jpg"
                alt="Ms. Supaporn Khamsom (Ava) - Founder & CEO"
                className="rounded-sm h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-gold/5 rounded-sm -z-10"></div>
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <div className="space-y-4">
                <h3 className="text-3xl md:text-4xl font-bold text-silver">
                  Ms. Supaporn Khamsom (Ava)
              
                </h3>
                <p className="text-gold text-lg font-semibold">Founder & Chief Executive Officer</p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  With an unwavering commitment to excellence and a passion for creating extraordinary experiences, 
                  Ava has transformed Prestige Events into Thailand's premier luxury event management company.
                </p>
                <p>
                  Her visionary leadership and meticulous attention to detail have set new standards in the industry. 
                  Under her guidance, Prestige Events has successfully executed over 500 high-profile events, earning 
                  the trust of Fortune 500 companies and discerning clients across Asia and beyond.
                </p>
                <p>
                  Known for her professional excellence and dedication to client satisfaction, Ava personally oversees 
                  every project to ensure flawless execution. Her ability to blend creativity with precision has made 
                  Prestige Events the go-to choice for those seeking nothing but the best.
                </p>
                <p className="text-gold italic">
                  "Every event is an opportunity to create lasting memories. We don't just plan events; we craft experiences 
                  that resonate for a lifetime."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indian Counterpart Section */}
      <section className="py-20 bg-gradient-to-b from-black to-dark-grey border-y border-gold/20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-gold text-sm tracking-widest uppercase mb-4">Our Global Presence</p>
              <h2 className="text-4xl md:text-5xl font-bold text-silver mb-4">
                Our Indian
                <span className="gold-gradient-text"> Counterpart</span>
              </h2>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto">
                Extending our legacy of excellence across borders, LikeWize brings the same commitment to 
                extraordinary experiences to India, ensuring seamless event management across continents.
              </p>
            </div>

            <div className="bg-dark-grey border-2 border-gold/20 rounded-sm p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="space-y-3 flex items-center gap-4">
                    <OptimizedImage
                      src="/likewize_logo.png"
                      alt="LikeWize"
                      className="h-16 w-auto object-contain"
                      loading="lazy"
                    />
                    <div>
                      <h3 className="text-3xl font-bold text-gold">LikeWize</h3>
                      <p className="text-gray-300 leading-relaxed mt-2">
                        Your trusted partner for world-class events in India, delivering the same excellence and 
                        attention to detail that defines the Prestige Events brand.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href="https://likewize.co.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-black border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                    >
                      <Globe className="text-gray-400 hover:text-gold" size={20} />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/likewizeindia"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-black border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                    >
                      <Linkedin className="text-gray-400 hover:text-gold" size={20} />
                    </a>
                    <a
                      href="https://www.youtube.com/@like_wize"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-black border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                    >
                      <Youtube className="text-gray-400 hover:text-gold" size={20} />
                    </a>
                    <a
                      href="https://www.instagram.com/like_wize_14?igsh=MTF4d3NhOGlxczlxcQ%3D%3D&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-black border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                    >
                      <Instagram className="text-gray-400 hover:text-gold" size={20} />
                    </a>
                  </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="text-gold flex-shrink-0 mt-1" size={20} />
                      <div className="space-y-1">
                        <a href="tel:+919870409708" className="block text-gray-300 hover:text-gold transition-colors">
                          +91 9870409708
                        </a>
                      <a href="tel:+919820480705" className="block text-gray-300 hover:text-gold transition-colors">
                        +91 9820480705
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Mail className="text-gold flex-shrink-0 mt-1" size={20} />
                    <a href="mailto:fenil@likewize.co.in" className="text-gray-300 hover:text-gold transition-colors break-all">
                      fenil@likewize.co.in
                    </a>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Globe className="text-gold flex-shrink-0 mt-1" size={20} />
                    <a href="https://likewize.co.in/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-gold transition-colors">
                      www.likewize.co.in
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center space-y-3 group hover:scale-105 transition-transform duration-300">
                <h3 className="text-5xl md:text-6xl font-bold gold-gradient-text">{stat.value}</h3>
                <p className="text-gray-400 text-sm tracking-wide uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Values</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              What Drives
              <span className="gold-gradient-text"> Us</span>
            </h2>
            <p className="text-gray-300 text-lg">
              Our core values shape everything we do, from the events we create to the relationships we build.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-grey border border-gold/20 hover:border-gold p-8 rounded-sm transition-all duration-300 group hover:scale-105"
                >
                  <IconComponent className="text-gold mb-4 group-hover:scale-110 transition-transform" size={40} />
                  <h3 className="text-xl font-bold text-silver mb-3 group-hover:text-gold transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Target className="text-gold mx-auto" size={56} />
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Our Mission
            </h2>
            <p className="text-gray-300 text-xl leading-relaxed">
              To create unforgettable experiences that exceed expectations through innovative concepts, 
              flawless execution, and unwavering dedication to excellence. We believe every event is an 
              opportunity to tell a story, forge connections, and create memories that last a lifetime.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px]">
              <OptimizedImage
                src="https://images.unsplash.com/photo-1649076122102-c7e4724d3dda"
                alt="Why Choose Us"
                className="rounded-sm w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-gold/5 rounded-sm -z-10"></div>
            </div>

            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-gold text-sm tracking-widest uppercase">Why Choose Us</p>
                <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight">
                  Experience the
                  <span className="gold-gradient-text"> Prestige Difference</span>
                </h2>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold text-lg">1</span>
                  </div>
                  <div>
                    <h4 className="text-silver font-semibold text-lg mb-2">Bespoke Solutions</h4>
                    <p className="text-gray-300">Every event is uniquely tailored to your vision, preferences, and objectives.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold text-lg">2</span>
                  </div>
                  <div>
                    <h4 className="text-silver font-semibold text-lg mb-2">Expert Team</h4>
                    <p className="text-gray-300">Seasoned professionals with decades of combined experience in luxury events.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold text-lg">3</span>
                  </div>
                  <div>
                    <h4 className="text-silver font-semibold text-lg mb-2">Extensive Network</h4>
                    <p className="text-gray-300">Access to premium venues, top-tier vendors, and exclusive talent worldwide.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold text-lg">4</span>
                  </div>
                  <div>
                    <h4 className="text-silver font-semibold text-lg mb-2">End-to-End Service</h4>
                    <p className="text-gray-300">Comprehensive support from initial planning to final execution and beyond.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;